import { QueryClient, type QueryKey } from "@tanstack/react-query"
import compact from "lodash-es/compact"
import concat from "lodash-es/concat"
import eq from "lodash-es/eq"
import every from "lodash-es/every"
import find from "lodash-es/find"
import first from "lodash-es/first"
import keyBy from "lodash-es/keyBy"
import map from "lodash-es/map"
import reject from "lodash-es/reject"
import sortBy from "lodash-es/sortBy"
import tail from "lodash-es/tail"
import { DateTime } from "luxon"

import { ECoachingSessionsGroupQueryKey, ECoachingSessionsIndividualQueryKey } from "@/3514/config"
import type { TAttendanceModalFormRowData } from "@/3514/modals/attendance"
import type {
    TSessionExtraPartiesMutationResponse as TPartiesMutationResponse,
    TSessionRescheduleMutationResponse as TRescheduleMutationResponse,
    ISessionScheduleMutationsPayload as TScheduleMutationPayload,
    TSessionScheduleMutationResponse as TScheduleMutationResponse
} from "@/3514/modals/session-schedule"
import type {
    ICoachingSessionDto,
    ICoachingSessionParticipantDto,
    ICoachingSessionsQueryDto,
    ISessionExtraPartyDto,
    ISessionRescheduleRecordDto
} from "@/3514/types"
import { ESessionAttendanceStatus, ESessionModality } from "@/3514/types"

function _timeSorter(sessionDto: ICoachingSessionDto): number {
    return DateTime.fromISO(sessionDto.session_time).toMillis()
}

function coachingSessionsQueryOnCancelUpdater(
    {
        queryKey,
        sessionId,
        cancellerName,
        responseDto
    }: {
        queryKey: QueryKey
        sessionId: number
        cancellerName: string
        responseDto: ICoachingSessionDto
    },
    { qc }: { qc: QueryClient }
): void {
    qc.setQueryData<ICoachingSessionsQueryDto>(
        queryKey,
        ({
            not_scheduled_sessions,
            upcoming_sessions,
            ...rest
        }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            not_scheduled_sessions: sortBy<ICoachingSessionDto>(
                compact<ICoachingSessionDto>(concat<ICoachingSessionDto>(not_scheduled_sessions, responseDto)),
                _timeSorter
            ),
            upcoming_sessions: sortBy<ICoachingSessionDto>(
                map<ICoachingSessionDto, ICoachingSessionDto>(
                    upcoming_sessions,
                    (s: ICoachingSessionDto): ICoachingSessionDto =>
                        sessionId === s.session_id
                            ? { ...s, cancelled_at: DateTime.now().toISO(), cancelled_by: cancellerName }
                            : s
                ),
                _timeSorter
            )
        })
    )
}

async function coachingSessionsQueryOnScheduleUpdater(
    {
        queryKey,
        sessionId,
        response,
        vars
    }: {
        queryKey: QueryKey
        sessionId: number
        response: TScheduleMutationResponse
        vars: TScheduleMutationPayload
    },
    { qc }: { qc: QueryClient }
): Promise<void> {
    const sessionQueryDto: ICoachingSessionsQueryDto = qc.getQueryData<ICoachingSessionsQueryDto>(queryKey)

    const sessionDto: ICoachingSessionDto = find<ICoachingSessionDto>(sessionQueryDto.not_scheduled_sessions, {
        session_id: sessionId
    })

    await qc.setQueryData(
        queryKey,
        ({
            not_scheduled_sessions,
            upcoming_sessions,
            ...rest
        }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            not_scheduled_sessions: reject<ICoachingSessionDto>(not_scheduled_sessions, { session_id: sessionId }),
            upcoming_sessions: sortBy<ICoachingSessionDto>(
                compact<ICoachingSessionDto>(
                    concat<ICoachingSessionDto>(upcoming_sessions, {
                        ...sessionDto,
                        session_id: response.data,
                        session_time: vars.session_datetime,
                        allow_reschedule: true
                    })
                ),
                _timeSorter
            )
        })
    )
}

async function coachingSessionsQueryOnRescheduleUpdater(
    {
        queryKey,
        sessionId,
        userName,
        response,
        vars
    }: {
        queryKey: QueryKey
        sessionId: number
        userName: string
        response: TRescheduleMutationResponse
        vars: TScheduleMutationPayload
    },
    { qc }: { qc: QueryClient }
): Promise<void> {
    await qc.setQueryData(
        queryKey,
        ({ upcoming_sessions, ...rest }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            upcoming_sessions: sortBy<ICoachingSessionDto>(
                map<ICoachingSessionDto, ICoachingSessionDto>(
                    upcoming_sessions,
                    (s: ICoachingSessionDto): ICoachingSessionDto =>
                        sessionId === s.session_id
                            ? {
                                  ...s,
                                  allow_reschedule: true,
                                  session_id: response.data.session_id,
                                  session_time: vars.session_datetime,
                                  extra_parties_info: response.data.extra_parties_info,
                                  reschedule_history: compact<ISessionRescheduleRecordDto>(
                                      concat<ISessionRescheduleRecordDto>(s.reschedule_history, {
                                          id: s.reschedule_history.length + 1,
                                          from_date: s.session_time,
                                          to_date: vars.session_datetime,
                                          rescheduled_at: DateTime.now().toISODate(),
                                          rescheduled_by: userName
                                      })
                                  )
                              }
                            : s
                ),
                _timeSorter
            )
        })
    )
}

async function coachingSessionsQueryOnGiftUpdater(
    { queryKey, sessionId, responseDto }: { queryKey: QueryKey; sessionId: number; responseDto: ICoachingSessionDto },
    { qc }: { qc: QueryClient }
): Promise<void> {
    qc.setQueryData<ICoachingSessionsQueryDto>(
        queryKey,
        ({
            not_scheduled_sessions,
            passed_sessions,
            ...rest
        }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            not_scheduled_sessions: sortBy<ICoachingSessionDto>(
                compact<ICoachingSessionDto>(concat<ICoachingSessionDto>(not_scheduled_sessions, responseDto)),
                _timeSorter
            ),
            passed_sessions: map<ICoachingSessionDto, ICoachingSessionDto>(
                passed_sessions,
                (s: ICoachingSessionDto): ICoachingSessionDto =>
                    sessionId === s.session_id ? { ...s, is_gifted: true } : s
            )
        })
    )
}

async function coachingSessionsQueryOnAttendanceUpdater(
    {
        participantId,
        groupId,
        sessionId,
        sessionModality,
        formData
    }: {
        participantId: number
        groupId: number | null
        sessionId: number
        sessionModality: ESessionModality
        formData: TAttendanceModalFormRowData[]
    },
    { qc }: { qc: QueryClient }
): Promise<void> {
    const queryKey: QueryKey =
        sessionModality === ESessionModality.Individual
            ? ECoachingSessionsIndividualQueryKey({ participantId })
            : ECoachingSessionsGroupQueryKey({ groupId })

    const sessionsQueryDto: ICoachingSessionsQueryDto | undefined = qc.getQueryData<ICoachingSessionsQueryDto>(queryKey)

    const sessionDto: ICoachingSessionDto | undefined = !!sessionsQueryDto
        ? find<ICoachingSessionDto>(
              compact<ICoachingSessionDto>(
                  concat<ICoachingSessionDto>(
                      sessionsQueryDto.upcoming_sessions,
                      sessionsQueryDto.passed_sessions,
                      sessionsQueryDto.live_session
                  )
              ),
              { session_id: sessionId }
          )
        : undefined

    function _listItemUpdater(s: ICoachingSessionDto | null): ICoachingSessionDto | null {
        if (!sessionDto || !s) return s

        if (s.session_id !== sessionDto.session_id) return s

        const submittedParties: TAttendanceModalFormRowData[] = reject<TAttendanceModalFormRowData>(formData, {
            isExtraParty: false
        })
        const submittedParticipants: TAttendanceModalFormRowData[] = reject<TAttendanceModalFormRowData>(formData, {
            isExtraParty: true
        })

        const participantById: Record<number, ICoachingSessionParticipantDto> = keyBy<ICoachingSessionParticipantDto>(
            sessionDto.participants,
            "id"
        )

        const extraPartyById: Record<number, ISessionExtraPartyDto> = keyBy<ISessionExtraPartyDto>(
            sessionDto.extra_parties_info,
            "id"
        )

        return {
            ...sessionDto,
            marked_attendance: true,
            is_missed: every<TAttendanceModalFormRowData>(submittedParticipants, {
                attendance: ESessionAttendanceStatus.NotPresent
            }),
            extra_parties_info: map<TAttendanceModalFormRowData, ISessionExtraPartyDto>(
                submittedParties,
                (row: TAttendanceModalFormRowData): ISessionExtraPartyDto => ({
                    ...extraPartyById[row.participantId],
                    attendance_status: row.attendance
                })
            ),
            participants: map<TAttendanceModalFormRowData, ICoachingSessionParticipantDto>(
                submittedParticipants,
                (row: TAttendanceModalFormRowData): ICoachingSessionParticipantDto => ({
                    ...participantById[row.participantId],
                    attendance_status: row.attendance
                })
            )
        }
    }

    qc.setQueryData<ICoachingSessionsQueryDto>(
        queryKey,
        ({
            upcoming_sessions,
            passed_sessions,
            live_session,
            ...rest
        }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            live_session: _listItemUpdater(live_session),
            upcoming_sessions: map<ICoachingSessionDto, ICoachingSessionDto>(upcoming_sessions, _listItemUpdater),
            passed_sessions: map<ICoachingSessionDto, ICoachingSessionDto>(passed_sessions, _listItemUpdater)
        })
    )
}

async function coachingSessionsQueryOnSwapUpdater(
    {
        queryKey,
        sessionId,
        updatedSessionId,
        updatedTime
    }: { queryKey: QueryKey; sessionId: number; updatedSessionId: number; updatedTime: string },
    { qc }: { qc: QueryClient }
): Promise<void> {
    qc.setQueryData(
        queryKey,
        ({ upcoming_sessions, ...rest }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            upcoming_sessions: sortBy(
                map<ICoachingSessionDto, ICoachingSessionDto>(
                    upcoming_sessions,
                    (s: ICoachingSessionDto): ICoachingSessionDto =>
                        eq(s.session_id, sessionId)
                            ? {
                                  ...s,
                                  session_id: updatedSessionId,
                                  session_time: updatedTime
                              }
                            : s
                ),
                _timeSorter
            )
        })
    )
}

function coachingSessionsQueryExtraPartyUpdater(
    {
        mutationResponse,
        queryKey,
        sessionId
    }: {
        mutationResponse: TPartiesMutationResponse
        queryKey: QueryKey
        sessionId: number
    },
    { qc }: { qc: QueryClient }
): void {
    function _updateSessionListWithExtraParties(
        list: ICoachingSessionDto[],
        resp: TPartiesMutationResponse,
        sessionId: number
    ): ICoachingSessionDto[] {
        if (!find<ICoachingSessionDto>(list, { session_id: sessionId })) return list

        return map<ICoachingSessionDto, ICoachingSessionDto>(
            list,
            (s: ICoachingSessionDto): ICoachingSessionDto =>
                eq(sessionId, s.session_id) ? { ...s, extra_parties_info: resp.data } : s
        )
    }

    qc.setQueryData(
        queryKey,
        ({
            not_scheduled_sessions: unscheduled,
            upcoming_sessions: upcoming,
            ...rest
        }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            not_scheduled_sessions: _updateSessionListWithExtraParties(unscheduled, mutationResponse, sessionId),
            upcoming_sessions: _updateSessionListWithExtraParties(upcoming, mutationResponse, sessionId)
        })
    )
}

async function coachingSessionsQueryLiveSessionAdder(
    { queryKey }: { queryKey: QueryKey },
    { qc }: { qc: QueryClient }
): Promise<void> {
    const rawQueryData: ICoachingSessionsQueryDto = qc.getQueryData<ICoachingSessionsQueryDto>(queryKey)

    await qc.setQueryData(
        queryKey,
        ({ upcoming_sessions, ...rest }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            live_session: first<ICoachingSessionDto>(rawQueryData.upcoming_sessions),
            upcoming_sessions: tail<ICoachingSessionDto>(upcoming_sessions)
        })
    )
}

async function coachingSessionsQueryLiveSessionRemover(
    { queryKey }: { queryKey: QueryKey },
    { qc }: { qc: QueryClient }
): Promise<void> {
    await qc.setQueryData(
        queryKey,
        ({ live_session, passed_sessions, ...rest }: ICoachingSessionsQueryDto): ICoachingSessionsQueryDto => ({
            ...rest,
            live_session: null,
            passed_sessions: sortBy<ICoachingSessionDto>(
                compact<ICoachingSessionDto>(
                    concat<ICoachingSessionDto>(passed_sessions, {
                        ...live_session,
                        allow_swap: false,
                        allow_reschedule: false
                    })
                ),
                _timeSorter
            )
        })
    )
}

export {
    coachingSessionsQueryOnCancelUpdater,
    coachingSessionsQueryOnScheduleUpdater,
    coachingSessionsQueryOnRescheduleUpdater,
    coachingSessionsQueryOnGiftUpdater,
    coachingSessionsQueryOnAttendanceUpdater,
    coachingSessionsQueryOnSwapUpdater,
    coachingSessionsQueryExtraPartyUpdater,
    coachingSessionsQueryLiveSessionAdder,
    coachingSessionsQueryLiveSessionRemover
}
